/**
 * VariantMappingPreview — Story 11.5.
 *
 * Live preview do template HSM renderizado com sample values dos mappings.
 * Mostra placeholders ainda não mapeados como `{{N}}` literais (fallback
 * schema padrão) + warnings UX (cross-ref MEDIUM-3 do gate 11.4b).
 *
 * Client-side pure render (zero latency) — backend POST /preview existe pra
 * outros consumers mas aqui priorizamos UX responsiva ao typing.
 */

import { useMemo } from 'react';
import { AlertCircle } from 'lucide-react';

import { Label } from '@/components/ui/label';
import { renderTemplatePreview } from '@/lib/template-preview';

interface Props {
  contentPreview: string | null | undefined;
  variableMappings: Record<string, string>;
  /** Optional — nome do template renderizado, mostrado no cabeçalho do preview. */
  templateName?: string;
}

export function VariantMappingPreview({ contentPreview, variableMappings, templateName }: Props) {
  const preview = useMemo(
    () => renderTemplatePreview(contentPreview, variableMappings),
    [contentPreview, variableMappings],
  );

  if (!contentPreview) {
    return (
      <div className="rounded-md border border-dashed bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground">
        Selecione um template Meta acima pra ver o preview.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="variant-mapping-preview">
      <div className="flex items-center justify-between">
        <Label>Preview</Label>
        {templateName && (
          <span className="text-xs text-muted-foreground" data-testid="preview-template-name">
            {templateName}
          </span>
        )}
      </div>

      <div className="space-y-2 rounded-md border bg-muted/30 p-3">
        <pre className="whitespace-pre-wrap break-words text-sm" data-testid="preview-rendered">
          {preview.rendered}
        </pre>

        {preview.missingSlots.length > 0 && (
          <div
            data-testid="preview-missing-slots"
            className="flex items-start gap-2 rounded border border-amber-400/40 bg-amber-50 px-2 py-1 text-xs dark:border-amber-500/40 dark:bg-amber-950/30"
            role="status"
          >
            <AlertCircle
              className="mt-0.5 h-3 w-3 shrink-0 text-amber-700 dark:text-amber-400"
              aria-hidden
            />
            <span className="text-amber-900 dark:text-amber-200">
              {preview.missingSlots.join(', ')} sem mapping — usa schema padrão do template Meta.
            </span>
          </div>
        )}

        {preview.warnings.length > 0 && (
          <ul
            data-testid="preview-warnings"
            className="space-y-1 rounded border border-amber-400/40 bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-200"
          >
            {preview.warnings.map((w, i) => (
              <li key={i}>⚠️ {w}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
